//  Tracks all translation keys used during server-side rendering
//  compares them against keys returned by Tolgee API

declare global {
  // eslint-disable-next-line no-var, vars-on-top
  var __tolgeeReturnedKeys: Record<string, string> | undefined;
}

interface UsedKeyTracker {
  usedKeys: Set<string>;
  routeTags: string[];
  language: string;
  pageName: string;
}

// Global tracker for current SSR request
let currentTracker: UsedKeyTracker | null = null;

// init tracking new ssr request
export const initializeKeyTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
): void => {
  currentTracker = {
    usedKeys: new Set<string>(),
    routeTags,
    language,
    pageName,
  };
};

export const trackKeyUsage = (key: string, isDynamic = false): void => {
  // Skip tracking for dynamic keys - they won't trigger Sentry alerts
  if (isDynamic) return;

  if (currentTracker && key && typeof key === "string") {
    currentTracker.usedKeys.add(key);
  }
};

// check for dynamic/static - requireDot: true for critical keys only
export const isValidTranslationKey = (key: string, requireDot = false): boolean => {
  // Valid keys----> dot notation, lowercase with dots

  if (!key || key.length < 3) return false;

  if (
    key.includes(" ") ||
    /^[A-Z]/.test(key) ||
    /^[0-9]/.test(key) ||
    key.includes("@") ||
    key.includes("+") ||
    /^https?:\/\//.test(key) ||
    key.length > 100
  ) {
    return false;
  }

  if (requireDot) {
    return key.includes(".");
  }

  if (!key.includes(".")) {
    return /^[a-z]+$/.test(key);
  }

  return true;
};

const isDotFormattedKey = (key: string): boolean => key.includes(".");

type TranslationAlertOptions = {
  level: "warning" | "error";
  tags: Record<string, string>;
  extra: Record<string, unknown>;
  fingerprint?: string[];
};

/** Send translation alerts from SSR (@sentry/node) or CSR (window.Sentry). */
const captureTranslationAlert = (message: string, options: TranslationAlertOptions): void => {
  if (typeof window !== "undefined" && typeof window.Sentry !== "undefined") {
    window.Sentry.captureMessage(message, options);
    return;
  }

  if (typeof global !== "undefined" && __SERVER__) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const Sentry = require("@sentry/node");
      Sentry.captureMessage(message, options);
    } catch {
      // Sentry not initialized — server logs remain the fallback
    }
  }
};

export const reportEmptyTranslationLoad = ({
  language,
  routeTags,
  pageName,
  source,
}: {
  language: string;
  routeTags: string[];
  pageName: string;
  source: "ssr" | "csr";
}): void => {
  captureTranslationAlert("Critical: Translation load returned zero keys", {
    level: "error",
    fingerprint: ["empty-translation-load", pageName, source],
    tags: {
      page: pageName,
      language,
      source,
      routeTags: routeTags.join(","),
      severity: "critical",
    },
    extra: {
      type: "EMPTY_TRANSLATION_LOAD",
      language,
      routeTags,
      page: pageName,
      source,
      timestamp: new Date().toISOString(),
    },
  });
};

export const getCurrentTracker = (): UsedKeyTracker | null => currentTracker;

export const compareAndAlert = (
  returnedKeys: Record<string, string>,
  isLoadingTranslations = false,
): void => {
  if (!currentTracker) {
    return;
  }

  // Skip Sentry alerts if translations are still loading to avoid false positives
  if (isLoadingTranslations) {
    return;
  }

  const { usedKeys, routeTags, language, pageName } = currentTracker;
  const returnedKeySet = new Set(Object.keys(returnedKeys));

  // Separate valid translation keys from dynamic/hardcoded strings
  const allUsedKeys = Array.from(usedKeys);
  const validKeys = allUsedKeys.filter((key) => isValidTranslationKey(key));
  // const invalidKeys = allUsedKeys.filter((key) => !isValidTranslationKey(key)); //keeping this for future usage

  // Find missing keys (valid keys that aren't returned by Tolgee)
  const missingKeys = validKeys.filter((key) => !returnedKeySet.has(key));
  const missingDotFormattedKeys = missingKeys.filter(isDotFormattedKey);

  // const foundKeys = validKeys.filter((key) => returnedKeySet.has(key));  // can be used to list all the found keys from tolgee

  if (typeof window !== "undefined" && (window as any).__reduxDispatch) {
    (window as any).__reduxDispatch({
      type: "appData/setMissingTranslationKeys",
      payload: missingKeys,
    });
  }

  if (missingKeys.length > 0) {
    const alertPayload = {
      type: "MISSING_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      missingKeys,
      validKeyCount: validKeys.length,
      returnedKeyCount: returnedKeySet.size,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Missing Translation Keys Detected", {
      level: "warning",
      tags: {
        page: pageName,
        language,
        missingKeyCount: String(missingKeys.length),
      },
      extra: alertPayload,
    });
  }

  // critical alert for missing keys
  if (missingDotFormattedKeys.length > 0) {
    const dotFormattedAlertPayload = {
      type: "MISSING_DOTFORMATTED_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      missingDotFormattedKeys,
      missingKeyCount: missingDotFormattedKeys.length,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Critical: Missing Dot-Formatted Translation Keys", {
      level: "error",
      fingerprint: ["missing-translation-keys", pageName, missingDotFormattedKeys.join(",")],
      tags: {
        page: pageName,
        language,
        missingKeyCount: String(missingDotFormattedKeys.length),
        missingDotFormattedKeys: missingDotFormattedKeys.join(","),
        routeTags: routeTags.join(","),
        severity: "critical",
      },
      extra: dotFormattedAlertPayload,
    });
  }
};

export const performPostRenderComparison = (): void => {
  if (typeof global !== "undefined" && global.__tolgeeReturnedKeys) {
    compareAndAlert(global.__tolgeeReturnedKeys);

    delete global.__tolgeeReturnedKeys;
    resetTracking();
  }
};

export const resetTracking = (): void => {
  currentTracker = null;
};

//  Client-side tracking for SPA navigation
export const initializeClientSideTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
): void => {
  initializeKeyTracking(routeTags, language, pageName);
};

export const performClientSideComparison = (
  returnedKeys: Record<string, string>,
  getIsLoading?: () => boolean,
): void => {
  setTimeout(() => {
    if (currentTracker) {
      const isLoading = getIsLoading ? getIsLoading() : false;
      compareAndAlert(returnedKeys, isLoading);
      resetTracking();
    }
  }, 3000);
};
